import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const brandLogos = [
  "https://oogiebear.com/cdn/shop/files/logo.png?v=1698696694",
  "https://mesodermrx.com/cdn/shop/files/logo.png?v=1734912158&width=210",
  "https://shopeasyblinds.com/cdn/shop/files/EasyBlinds_Logo_Blue_220x.png?v=1690242255",
  "https://www.hbastore.com/images/logo.png",
  "https://g4m5y2d2.delivery.rocketcdn.me/wp-content/uploads/elementor/thumbs/POLARIS-LOGO-320x110px-GLASS-HINGE-SPECIALISTS-App-01-scaled-rakie0eglkqnptvq7gz995alvpiz89u7vgor9dvpo0.png",
  "https://www.farmy.ch/resources/farmy/images/farmy_main_logo_2021.svg",
];

const BrandsCarousel = () => {
  const sectionRef = useRef(null);
  const stripRef = useRef(null);
  const titleRef = useRef(null);
  const orbRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const strip = stripRef.current;
    const title = titleRef.current;
    const orb = orbRef.current;

    // Floating orb rotation
    gsap.to(orb, {
      rotation: 360,
      duration: 20,
      repeat: -1,
      ease: "none",
    });

    gsap.to(orb, {
      y: -30,
      duration: 4,
      repeat: -1,
      yoyo: true,
      ease: "power2.inOut",
    });

    // Title animation
    gsap.fromTo(
      title,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
        },
      }
    );

    // Infinite carousel (faster)
    const totalWidth = strip.scrollWidth / 2;
    gsap.to(strip, {
      x: -totalWidth,
      duration: 30, // decreased from 50 to speed up
      repeat: -1,
      ease: "none",
    });

    // Logos fade-in (no zoom effect)
    gsap.fromTo(
      ".brand-logo",
      { opacity: 0.8 },
      {
        opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        scrollTrigger: {
          trigger: strip,
          start: "top 90%",
        },
      }
    );
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        position: "relative",
        background: "linear-gradient(180deg, #000 0%, #0a0a0a 50%, #000 100%)",
        padding: "6rem 0",
        overflow: "hidden",
        borderRadius: "0px",
      }}
    >
      {/* Floating Orbs */}
      <div
        ref={orbRef}
        style={{
          position: "absolute",
          width: "400px",
          height: "400px",
          background: "radial-gradient(circle, rgba(255, 215, 0, 0.15) 0%, transparent 70%)",
          borderRadius: "50%",
          top: "10%",
          right: "-10%",
          filter: "blur(80px)",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          width: "300px",
          height: "300px",
          background: "radial-gradient(circle, rgba(0, 217, 255, 0.12) 0%, transparent 70%)",
          borderRadius: "50%",
          bottom: "20%",
          left: "-5%",
          filter: "blur(60px)",
          animation: "float 6s ease-in-out infinite reverse",
          zIndex: 1,
        }}
      />

      {/* Particles */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `
            radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.1), transparent),
            radial-gradient(2px 2px at 40px 70px, rgba(255, 215, 0, 0.1), transparent),
            radial-gradient(1px 1px at 90px 40px, rgba(0, 217, 255, 0.1), transparent)
          `,
          backgroundRepeat: "repeat",
          backgroundSize: "150px 100px",
          animation: "particleFloat 20s linear infinite",
          zIndex: 1,
        }}
      />

      <div style={{ maxWidth: "1400px", margin: "0 auto", position: "relative", zIndex: 10 }}>
        {/* Ribbon */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "4rem",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.75rem 1.5rem",
              background: "rgba(208, 240, 192, 0.6)", // light green
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(192, 240, 255, 0.4)", // light blue
              borderRadius: "50px",
              fontSize: "0.95rem",
              fontWeight: "600",
              marginBottom: "2rem",
              color: "#333",
              boxShadow: "0 4px 15px rgba(192, 240, 255, 0.15)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "-50%",
                width: "50%",
                height: "100%",
                background: "linear-gradient(120deg, transparent, rgba(255, 248, 192, 0.5), transparent)", // yellow shimmer
                transform: "skewX(-20deg)",
                animation: "shimmer 3s infinite",
              }}
            />
            ⭐ Trusted Partnerships
          </div>

          {/* Section Title */}
          <h2
            ref={titleRef}
            style={{
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              fontWeight: "900",
              lineHeight: "1.2",
              marginBottom: "1.5rem",
              color: "#fff",
            }}
          >
            Our{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #FFD700, #00D9FF, #9333EA)",
                backgroundSize: "200% 100%",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                animation: "gradientShift 3s ease-in-out infinite",
              }}
            >
              Elite
            </span>{" "}
            Partners
          </h2>

          <p
            style={{
              fontSize: "clamp(1rem, 1.5vw, 1.2rem)",
              color: "rgba(255, 255, 255, 0.8)",
              lineHeight: "1.7",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Collaborating with industry leaders to deliver exceptional results and drive unprecedented growth.
          </p>
        </div>

        {/* Logos Carousel */}
        <div
          style={{
            position: "relative",
            background: "rgba(255, 255, 255, 0.02)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "24px",
            padding: "3rem 0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.5), transparent)",
            }}
          />

          <div
            ref={stripRef}
            style={{
              display: "flex",
              whiteSpace: "nowrap",
              width: "max-content",
            }}
          >
            {[...brandLogos, ...brandLogos].map((logo, index) => (
              <img
                key={index}
                src={logo}
                alt={`Brand ${index}`}
                className="brand-logo"
                style={{
                  height: "50px", // smaller logos
                  margin: "0 40px",
                  objectFit: "contain",
                  filter: "brightness(0) invert(1) opacity(0.8)",
                  transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  cursor: "pointer",
                  transform: "scale(1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = "brightness(0) invert(1) opacity(1)";
                  e.currentTarget.style.transform = "scale(1.1) translateY(-5px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(255, 215, 0, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = "brightness(0) invert(1) opacity(0.8)";
                  e.currentTarget.style.transform = "scale(1) translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes particleFloat {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-100px); }
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes shimmer {
          0% { left: -50%; }
          100% { left: 120%; }
        }
      `}</style>
    </section>
  );
};

export default BrandsCarousel;
